import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProductTour, { TOUR_DONE_KEY } from '../components/ProductTour';
import {
  LayoutDashboard, Store, UtensilsCrossed, Users,
  TrendingUp, Settings, LogOut, ChevronLeft, Menu, Heart, Megaphone, ShieldCheck,
  Boxes, FileText, CalendarClock, Settings2, ChefHat, CreditCard,
  Map, ClipboardList, Shield, Banknote, BookOpen, Gift, Mail, CalendarDays, Building2, BarChart3, UserCog, HelpCircle, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { UserMenu } from '../components/UserMenu';
import { BentaLogo, BentaMark } from '../components/BentaLogo';

export const sidebarItems = [
  { path: '/getting-started', icon: Rocket, label: 'Getting Started', roles: ['owner'] },
  { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', roles: ['owner', 'store_manager', 'finance_manager'], exact: true },
  { path: '/pos', icon: CreditCard, label: 'POS', roles: ['owner', 'store_manager', 'cashier'] },
  { path: '/pos/tables', icon: Map, label: 'Table Map', roles: ['owner', 'store_manager', 'cashier'] },
  { path: '/kds', icon: ChefHat, label: 'Kitchen Display', roles: ['owner', 'store_manager', 'kitchen_staff'] },
  { path: '/backoffice/kitchen-stations', icon: ChefHat, label: 'Kitchen Stations', roles: ['owner', 'store_manager'] },
  { path: '/backoffice/reservations', icon: CalendarDays, label: 'Reservations', roles: ['owner', 'store_manager', 'cashier'] },
  { divider: true, label: 'Management' },
  { path: '/dashboard/menu', icon: UtensilsCrossed, label: 'Menu', roles: ['owner', 'store_manager'] },
  { path: '/backoffice/inventory', icon: Boxes, label: 'Inventory', roles: ['owner', 'store_manager', 'inventory_manager'] },
  { path: '/backoffice/recipes', icon: FileText, label: 'Recipes & Cost', roles: ['owner', 'store_manager'] },
  { path: '/backoffice/customers', icon: Heart, label: 'Customers / CRM', roles: ['owner', 'marketing_crm'] },
  { path: '/dashboard/promotions', icon: Megaphone, label: 'Promotions', roles: ['owner', 'store_manager', 'marketing_crm'] },
  { path: '/backoffice/marketing', icon: Mail, label: 'Marketing', roles: ['owner', 'marketing_crm'] },
  { path: '/backoffice/gift-cards', icon: Gift, label: 'Gift Cards', roles: ['owner', 'store_manager', 'cashier'] },
  { path: '/backoffice/employees', icon: UserCog, label: 'Employees', roles: ['owner', 'store_manager'] },
  { path: '/backoffice/cash-management', icon: Banknote, label: 'Cash Management', roles: ['owner', 'store_manager', 'finance_manager'] },
  { divider: true, label: 'Reports & Finance' },
  { path: '/backoffice/reports-hub', icon: BarChart3, label: 'Reports Hub', roles: ['owner', 'store_manager', 'finance_manager'] },
  { path: '/finance/reports', icon: TrendingUp, label: 'Reports', roles: ['owner', 'store_manager', 'finance_manager'] },
  { path: '/finance/bir-reports', icon: ShieldCheck, label: 'BIR Reports', roles: ['owner', 'store_manager', 'finance_manager'] },
  { path: '/backoffice/accounting', icon: BookOpen, label: 'Accounting', roles: ['owner', 'finance_manager'] },
  { path: '/finance/audit', icon: ClipboardList, label: 'Audit Logs', roles: ['owner', 'finance_manager'] },
  { path: '/backoffice/report-scheduling', icon: CalendarClock, label: 'Scheduled Reports', roles: ['owner', 'store_manager'] },
  { divider: true, label: 'Administration' },
  { path: '/backoffice/branches', icon: Building2, label: 'Branches', roles: ['owner'] },
  { path: '/dashboard/outlets', icon: Store, label: 'Outlets', roles: ['owner'] },
  { path: '/dashboard/users', icon: Users, label: 'Users & Roles', roles: ['owner'] },
  { path: '/backoffice/outlet-settings', icon: Settings2, label: 'Outlet Config', roles: ['owner'] },
  { path: '/dashboard/settings', icon: Settings, label: 'Settings', roles: ['owner'] },
  { path: '/platform/admin', icon: Shield, label: 'Platform Admin', roles: ['Super Admin'] },
  { divider: true, label: 'Help' },
  { path: '/manual', icon: HelpCircle, label: 'User Manual', always: true },
];

const TOUR_STEPS = [
  { title: 'Welcome to BENTA 👋', body: 'A quick 60-second tour of where everything lives. You can replay it anytime with “Take a tour” in the top bar.' },
  { selector: '[data-testid="nav-getting-started"]', title: 'Getting Started', body: 'Your setup checklist — come back here anytime to finish or review your setup.' },
  { selector: '[data-testid="nav-overview"]', title: 'Overview', body: 'Your daily numbers at a glance — sales, transactions, guests and average ticket.' },
  { selector: '[data-testid="nav-pos"]', title: 'Point of Sale', body: 'Take orders and accept payment here — cash, card, GCash, Maya, or split across methods.' },
  { selector: '[data-testid="nav-table-map"]', title: 'Table Map', body: 'Seat guests, open table orders, and transfer / split / merge tables for dine-in.' },
  { selector: '[data-testid="nav-menu"]', title: 'Menu', body: 'Create categories and products, set prices, and bulk-upload with a CSV template.' },
  { selector: '[data-testid="nav-inventory"]', title: 'Supplies & Inventory', body: 'Manage stock, suppliers and purchase orders. FIFO costing feeds your cost of goods.' },
  { selector: '[data-testid="nav-reports"]', title: 'Reports', body: 'Sales, cost-of-goods and margin. BIR X/Z readings live under BIR Reports.' },
  { selector: '[data-testid="nav-users---roles"]', title: 'Users & Roles', body: 'Create logins for your staff — each role only sees what it needs.' },
  { selector: '[data-testid="nav-settings"]', title: 'Settings', body: 'Business, receipt and outlet configuration all live here.' },
  { title: "You're all set! 🎉", body: 'That’s the grand tour. Tap “Take a tour” in the top bar to replay it whenever you like.' },
];

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, currentOutlet, logout, isPlatformAdmin, isOwner, setupCompleted } = useAuth();
  // Tablets in portrait get the icon rail so the page keeps the room.
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => {
    const h = () => setIsPhone(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  useEffect(() => { setDrawer(false); }, [location.pathname]);
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const [showTour, setShowTour] = useState(false);

  // Auto-run the tour once, for an owner whose initial setup is complete.
  useEffect(() => {
    let seen = false;
    try { seen = !!localStorage.getItem(TOUR_DONE_KEY); } catch { /* ignore */ }
    if (isOwner && setupCompleted && !seen) {
      const t = setTimeout(() => setShowTour(true), 900);
      return () => clearTimeout(t);
    }
  }, [isOwner, setupCompleted]);

  // Allow replaying from anywhere (e.g. the Getting Started page)
  useEffect(() => {
    const h = () => setShowTour(true);
    window.addEventListener('benta:start-tour', h);
    return () => window.removeEventListener('benta:start-tour', h);
  }, []);

  const userRole = isPlatformAdmin ? 'Super Admin' : user?.role_name || '';

  const filteredItems = sidebarItems.filter(item => item.divider || item.always || item.roles.includes(userRole));

  const cleanedItems = filteredItems.filter((item, idx, arr) => {
    if (!item.divider) return true;
    if (idx === 0) return false;
    if (idx === arr.length - 1) return false;
    if (arr[idx - 1]?.divider) return false;
    return arr.slice(idx + 1).some(i => !i.divider);
  });

  const compact = collapsed && !isPhone;
  const handleLogout = () => { logout(); navigate('/login'); };

  const activeItem = cleanedItems.find(i => !i.divider && (i.exact ? location.pathname === i.path : location.pathname.startsWith(i.path)));

  return (
    <div className="h-screen flex bg-background" data-testid="dashboard-layout">
      {drawer && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setDrawer(false)} data-testid="drawer-backdrop" />}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 ${compact ? 'md:w-[72px]' : 'md:w-64'} md:static md:z-auto md:translate-x-0 ${drawer ? 'translate-x-0' : '-translate-x-full'} bg-card border-r border-border flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="h-20 px-4 border-b border-border flex items-center justify-between">
          {!compact ? (
            <Link to="/dashboard" className="flex items-center" data-testid="sidebar-logo">
              <BentaLogo className="h-16 w-auto max-w-[190px]" />
            </Link>
          ) : (
            <Link to="/dashboard" className="mx-auto" data-testid="sidebar-logo-mark">
              <BentaMark className="h-9 w-9" />
            </Link>
          )}
          {!compact && (
            <Button variant="ghost" size="icon" onClick={() => (isPhone ? setDrawer(false) : setCollapsed(true))} className="text-muted-foreground" data-testid="sidebar-collapse-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
        {compact && (
          <div className="flex justify-center py-2 border-b border-border">
            <Button variant="ghost" size="icon" onClick={() => setCollapsed(false)} className="text-muted-foreground" data-testid="sidebar-expand-btn">
              <Menu className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {cleanedItems.map((item, idx) => {
              if (item.divider) {
                return (
                  <div key={`divider-${idx}`} className="pt-5 pb-1.5">
                    {!compact
                      ? <p className="px-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-[0.12em]">{item.label}</p>
                      : <div className="h-px bg-border mx-2" />}
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''} ${compact ? 'justify-center px-2' : ''}`}
                  title={item.label}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!compact && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <button
            onClick={handleLogout}
            className={`sidebar-item w-full text-destructive hover:bg-destructive/10 hover:text-destructive ${compact ? 'justify-center px-2' : ''}`}
            data-testid="dashboard-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!compact && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border flex items-center justify-between px-3 lg:px-6 gap-2 bg-card">
          <Button variant="ghost" size="icon" className="md:hidden shrink-0 -ml-1" onClick={() => setDrawer(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-xl lg:text-2xl font-bold text-foreground leading-none tracking-tight truncate">
              {activeItem?.label || 'Dashboard'}
            </h1>
            {currentOutlet && <p className="text-sm text-muted-foreground mt-0.5">{currentOutlet.name}</p>}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setShowTour(true)} data-testid="take-tour-btn">
              <Rocket className="w-4 h-4 lg:mr-1.5" /> <span className="hidden lg:inline">Take a tour</span>
            </Button>
            <UserMenu compact={isPhone} />
          </div>
        </header>

        {/* Page Content */}
        <ScrollArea className="flex-1 bg-background">
          <div className="p-4 lg:p-8 animate-fadeIn">
            <Outlet />
          </div>
        </ScrollArea>
      </main>

      <ProductTour run={showTour} steps={TOUR_STEPS} onClose={() => setShowTour(false)} />
    </div>
  );
};

export default DashboardLayout;
